/**
 * WizardCard — direzione 03 "Aria" (DEC-10, 2026-09-22).
 * Riproduce `design/directions/d03-aria.html` componendo i pezzi del design system:
 * fascia sfumata con progresso in parole, card bianca, titolo bold, pillole, campo libero,
 * Invia nella stessa sfumatura della fascia, vie d'uscita, "Chiedi a mia Tina".
 *
 * Presentazionale: nessuna chiamata di rete. Il comportamento arriva nella lane B
 * attraverso le callback opzionali.
 */
import type { ReactNode } from 'react'

import type { WizardAnswerKind } from '../types'
import { AnswerField } from './AnswerField'
import { AskAgentLink } from './AskAgentLink'
import { ChoiceChip } from './ChoiceChip'
import { PrimaryButton } from './PrimaryButton'
import { ProgressWords } from './ProgressWords'
import { QuestionTitle } from './QuestionTitle'
import { SecondaryButton } from './SecondaryButton'
import { WhyText } from './WhyText'

export type WizardCardProps = {
  /** "Le sue giornate" — il blocco, in testo normale: niente pretitolo maiuscolo. */
  block: string
  /** "Parte 3 di 12" — progresso in parole. */
  progress: string | { current: number; total: number }
  question: string
  /** Perche lo chiedo: una frase, mai una giustificazione lunga. */
  why: string
  choices: readonly string[]
  fieldLabel: string
  /** Le vie d'uscita sono sempre disponibili (API §3). */
  skipKinds: readonly { kind: WizardAnswerKind; label: string }[]
  askAgentLabel: string
  /** Chiave della bozza del campo libero; di default deriva dalla domanda. */
  draftKey?: string
  submitLabel?: string
  selectedChoice?: string
  onChoice?: (choice: string) => void
  onTextChange?: (value: string) => void
  onSubmit?: () => void
  onSkip?: (kind: WizardAnswerKind) => void
  onAskAgent?: () => void
}

export function WizardCard({
  block,
  progress,
  question,
  why,
  choices,
  fieldLabel,
  skipKinds,
  askAgentLabel,
  draftKey,
  submitLabel = 'Invia',
  selectedChoice,
  onChoice,
  onTextChange,
  onSubmit,
  onSkip,
  onAskAgent,
}: WizardCardProps) {
  return (
    <WizardFrame band={<ProgressWords block={block} progress={progress} />}>
      <QuestionTitle>{question}</QuestionTitle>
      <div className="mb-auto">
        <WhyText>{why}</WhyText>
      </div>

      <div className="mt-[6px] flex flex-wrap gap-x-2 gap-y-[10px]">
        {choices.map((choice) => (
          <ChoiceChip key={choice} label={choice} selected={choice === selectedChoice} onSelect={onChoice} />
        ))}
      </div>

      <AnswerField label={fieldLabel} draftKey={draftKey ?? `wizard:${question}`} onChange={onTextChange} />

      <PrimaryButton onClick={onSubmit}>{submitLabel}</PrimaryButton>

      <div className="mt-3 flex flex-wrap gap-2.5">
        {skipKinds.map(({ kind, label }) => (
          <SecondaryButton key={kind} grow onClick={() => onSkip?.(kind)}>
            {label}
          </SecondaryButton>
        ))}
      </div>

      <AskAgentLink label={askAgentLabel} onAsk={onAskAgent} />
    </WizardFrame>
  )
}

/** Impianto della schermata: colonna mobile, fascia in alto, card che riempie il resto. */
export function WizardFrame({ band, children }: { band?: ReactNode; children: ReactNode }) {
  return (
    <div className="mx-auto flex min-h-full w-full max-w-[430px] flex-col px-[20px] pb-[28px] pt-[56px]">
      {band}
      <div className="surface-card flex flex-1 flex-col rounded-card px-[22px] pb-[22px] pt-[28px]">
        {children}
      </div>
    </div>
  )
}
